/**
 * Parametric character builder (our own art): face shape × ears × eyes × brows × mouth ×
 * colour, plus position sliders (eye distance / height, mouth height). Renders to SVG markup for
 * a 200 x 200 viewBox, so the same code draws profile avatars, the lesson overlay and the journal.
 */
import { circle, ellipse, arc, poly, smooth, heart, star } from './content/geometry';

export const FACE_SHAPES = ['round', 'oval', 'square', 'pear', 'heart', 'bean'] as const;
export const EAR_TYPES = ['none', 'cat', 'bear', 'dog', 'bunny', 'fox', 'mouse'] as const;
export const EYE_TYPES = ['dot', 'round', 'sparkle', 'sleepy', 'wink', 'happy'] as const;
export const BROW_TYPES = ['none', 'angry', 'surprised', 'curious', 'worried', 'calm'] as const;
export const MOUTH_TYPES = ['happy', 'bigSmile', 'sad', 'scared', 'open', 'tongue'] as const;
export const CHARACTER_COLORS = ['#FFB27A', '#FFC23A', '#5AD1FF', '#3DDC84', '#B98BFF', '#FF5C7A', '#FFC6D9', '#D8D2C8'] as const;

export interface CharacterSpec {
  face: (typeof FACE_SHAPES)[number];
  ears: (typeof EAR_TYPES)[number];
  eyes: (typeof EYE_TYPES)[number];
  brows: (typeof BROW_TYPES)[number];
  mouth: (typeof MOUTH_TYPES)[number];
  /** -1 (close) .. 1 (far) */
  eyeDistance: number;
  /** -1 (high) .. 1 (low) */
  eyeHeight: number;
  /** -1 (high) .. 1 (low) */
  mouthHeight: number;
  color: string;
}

export const DEFAULT_CHARACTER: CharacterSpec = {
  face: 'round',
  ears: 'cat',
  eyes: 'round',
  brows: 'calm',
  mouth: 'happy',
  eyeDistance: 0,
  eyeHeight: 0,
  mouthHeight: 0,
  color: '#FFB27A',
};

/** Choice values per builder slot (sliders use three stops). */
export const CHARACTER_CHOICES = {
  face: FACE_SHAPES,
  ears: EAR_TYPES,
  eyes: EYE_TYPES,
  brows: BROW_TYPES,
  mouth: MOUTH_TYPES,
  eyeDistance: [-1, 0, 1],
  eyeHeight: [-1, 0, 1],
  mouthHeight: [-1, 0, 1],
  color: CHARACTER_COLORS,
} as const;

const INK = '#132630';

function facePath(f: CharacterSpec['face']): string {
  switch (f) {
    case 'round':
      return circle(100, 112, 62);
    case 'oval':
      return ellipse(100, 114, 54, 68);
    case 'square':
      return smooth([[44, 70], [100, 56], [156, 70], [162, 130], [148, 170], [100, 178], [52, 170], [38, 130]], true, 0.35);
    case 'pear':
      return smooth([[62, 80], [100, 58], [138, 80], [162, 136], [140, 176], [100, 182], [60, 176], [38, 136]], true);
    case 'heart':
      return smooth([[100, 76], [130, 54], [164, 82], [154, 136], [100, 180], [46, 136], [36, 82], [70, 54]], true, 0.45);
    case 'bean':
      return smooth([[50, 90], [92, 56], [150, 66], [164, 120], [138, 172], [84, 178], [44, 150]], true);
  }
}

function earsSvg(e: CharacterSpec['ears'], fill: string): string {
  const p = (d: string) => `<path d="${d}" fill="${fill}" stroke="${INK}" stroke-width="4" stroke-linejoin="round"/>`;
  switch (e) {
    case 'none':
      return '';
    case 'cat':
      return p(poly([[52, 82], [50, 26], [92, 58]], true)) + p(poly([[148, 82], [150, 26], [108, 58]], true));
    case 'fox':
      return p(poly([[50, 86], [40, 18], [96, 56]], true)) + p(poly([[150, 86], [160, 18], [104, 56]], true));
    case 'bear':
      return p(circle(54, 62, 22)) + p(circle(146, 62, 22));
    case 'mouse':
      return p(circle(46, 64, 30)) + p(circle(154, 64, 30));
    case 'dog':
      return p(ellipse(40, 104, 18, 40, 15)) + p(ellipse(160, 104, 18, 40, -15));
    case 'bunny':
      return p(ellipse(76, 34, 14, 44, -8)) + p(ellipse(124, 34, 14, 44, 8));
  }
}

function eyeSvg(type: CharacterSpec['eyes'], x: number, y: number, right: boolean): string {
  const s = (d: string, w = 4) => `<path d="${d}" fill="none" stroke="${INK}" stroke-width="${w}" stroke-linecap="round"/>`;
  const f = (d: string, fill = INK) => `<path d="${d}" fill="${fill}"/>`;
  switch (type) {
    case 'dot':
      return f(circle(x, y, 6));
    case 'round':
      return f(circle(x, y, 11)) + f(circle(x + 3, y - 4, 4), '#FFFBF4');
    case 'sparkle':
      return f(ellipse(x, y, 11, 14)) + f(star(x + 3, y - 4, 5, 2, 4), '#FFFBF4') + f(circle(x - 4, y + 5, 2), '#FFFBF4');
    case 'sleepy':
      return s(arc(x, y - 2, 10, 6, 20, 160));
    case 'wink':
      return right ? s(arc(x, y + 2, 10, 7, 200, 340)) : f(circle(x, y, 10)) + f(circle(x + 3, y - 3, 3.5), '#FFFBF4');
    case 'happy':
      return s(arc(x, y + 4, 10, 9, 200, 340));
  }
}

function browSvg(type: CharacterSpec['brows'], x: number, y: number, right: boolean): string {
  const dir = right ? 1 : -1;
  const s = (d: string) => `<path d="${d}" fill="none" stroke="${INK}" stroke-width="4" stroke-linecap="round"/>`;
  switch (type) {
    case 'none':
      return '';
    case 'angry':
      return s(poly([[x - 12 * dir, y - 6], [x + 10 * dir, y + 3]]));
    case 'surprised':
      return s(arc(x, y + 2, 12, 9, 200, 340));
    case 'curious':
      return right ? s(arc(x, y + 4, 12, 10, 200, 330)) : s(poly([[x - 11, y], [x + 11, y]]));
    case 'worried':
      return s(poly([[x - 12 * dir, y + 3], [x + 10 * dir, y - 6]]));
    case 'calm':
      return s(arc(x, y + 6, 11, 6, 210, 330));
  }
}

function mouthSvg(type: CharacterSpec['mouth'], y: number): string {
  const s = (d: string) => `<path d="${d}" fill="none" stroke="${INK}" stroke-width="4" stroke-linecap="round"/>`;
  const f = (d: string, fill: string) => `<path d="${d}" fill="${fill}" stroke="${INK}" stroke-width="4" stroke-linejoin="round"/>`;
  switch (type) {
    case 'happy':
      return s(arc(100, y - 8, 18, 12, 20, 160));
    case 'bigSmile':
      return f(`M78 ${y - 4}Q100 ${y + 26} 122 ${y - 4}Z`, '#FF5C7A');
    case 'sad':
      return s(arc(100, y + 10, 16, 10, 200, 340));
    case 'scared':
      return s(smooth([[80, y + 4], [88, y - 4], [96, y + 4], [104, y - 4], [112, y + 4], [120, y - 4]]));
    case 'open':
      return f(ellipse(100, y, 10, 13), '#FF5C7A');
    case 'tongue':
      return s(arc(100, y - 8, 16, 10, 20, 160)) + f(`M94 ${y + 1}Q100 ${y + 16} 106 ${y + 1}Z`, '#FF5C7A');
  }
}

/** SVG inner markup for viewBox 0 0 200 200. */
export function renderCharacter(c: CharacterSpec): string {
  const eyeDx = 26 + c.eyeDistance * 10;
  const eyeY = 104 + c.eyeHeight * 12;
  const mouthY = 146 + c.mouthHeight * 10;
  return [
    earsSvg(c.ears, c.color),
    `<path d="${facePath(c.face)}" fill="${c.color}" stroke="${INK}" stroke-width="4" stroke-linejoin="round"/>`,
    `<path d="${circle(100 - eyeDx - 14, mouthY - 12, 9)}" fill="#FF5C7A" opacity="0.35"/>`,
    `<path d="${circle(100 + eyeDx + 14, mouthY - 12, 9)}" fill="#FF5C7A" opacity="0.35"/>`,
    eyeSvg(c.eyes, 100 - eyeDx, eyeY, false),
    eyeSvg(c.eyes, 100 + eyeDx, eyeY, true),
    browSvg(c.brows, 100 - eyeDx, eyeY - 22, false),
    browSvg(c.brows, 100 + eyeDx, eyeY - 22, true),
    mouthSvg(c.mouth, mouthY),
  ].join('');
}

/** Ready-made profile avatars (character presets). */
export const AVATARS: { id: string; spec: CharacterSpec }[] = [
  { id: 'fox', spec: { ...DEFAULT_CHARACTER, ears: 'fox', color: '#FFB27A', eyes: 'round', mouth: 'happy' } },
  { id: 'cat', spec: { ...DEFAULT_CHARACTER, ears: 'cat', color: '#FFC23A', eyes: 'sparkle', mouth: 'bigSmile' } },
  { id: 'bear', spec: { ...DEFAULT_CHARACTER, ears: 'bear', color: '#D8D2C8', eyes: 'dot', mouth: 'happy', face: 'oval' } },
  { id: 'bunny', spec: { ...DEFAULT_CHARACTER, ears: 'bunny', color: '#FFC6D9', eyes: 'happy', mouth: 'tongue' } },
  { id: 'mouse', spec: { ...DEFAULT_CHARACTER, ears: 'mouse', color: '#B98BFF', eyes: 'round', mouth: 'open', face: 'pear' } },
  { id: 'dog', spec: { ...DEFAULT_CHARACTER, ears: 'dog', color: '#5AD1FF', eyes: 'wink', mouth: 'bigSmile', face: 'bean' } },
  { id: 'frog', spec: { ...DEFAULT_CHARACTER, ears: 'none', color: '#3DDC84', eyes: 'sparkle', mouth: 'happy', face: 'square', eyeHeight: -1 } },
  { id: 'kid', spec: { ...DEFAULT_CHARACTER, ears: 'none', color: '#FFB27A', eyes: 'round', brows: 'curious', mouth: 'happy', face: 'heart' } },
];

export const avatarSpec = (id: string): CharacterSpec => AVATARS.find((a) => a.id === id)?.spec ?? AVATARS[0]!.spec;

// Used by sticker / prop rendering elsewhere.
export { heart as heartPath };
